/**
 * @file        connection/target.js
 *
 *              Implements the DCPv5 Target class. Instanciating this class lets the developers
 *              implement a basic network daemon that understands DCP; this is the basis for the
 *              Operation Router in current (Dec 2022) versions of the DCP scheduler and bank. The
 *              current implementation assumes that transports are built upon HTTP (with or without TLS)
 *              and provides a suitable bearer for this. Status URLs are automatically wired up, and a
 *              PseudoExpress (similar to a subset of Express.js) instance is provided for use of the
 *              transport Listener instances. We currently only support Socket.io listeners, but it
 *              should be possible to wire up other transports to the same bearer, provided they have a
 *              request which is differentiable from Socket.io's ?EIO pattern.
 *
 *              The basic architecture is that this class is responsible for providing a common bearer,
 *              and instanciating the Listener class from the available transports. Then we watch for
 *              transport-level connections, and use them to instanciate DCP sessions (Connection class).
 *              When the transport disconnect and reconnects, we wire up the new transport instance to
 *              the DCP session, provided it happens before linger timeout. This is how we support long-
 *              lived connections on devices whose network addresses change, network availability is
 *              intermittent, etc.
 *
 * @author      Wes Garland, wes@distributive.network
 * @date        March 2022, Nov 2023
 */
'use strict';

const chalk = require('dcp/utils').chalk();

const { EventEmitter }  = require('events');
const { Reference }     = require('dcp/common/dcp-timers');
const { assert }        = require('dcp/common/dcp-assert');
const { DcpURL }        = require('dcp/common/dcp-url');
const { DCPError }      = require('dcp/common/dcp-error');
const { PseudoExpress } = require('../node-libs/pseudo-express');
const { Connection }    = require('./connection');
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const { leafMerge }     = require('dcp/utils/obj-merge');
const { a$sleep }       = require('dcp/utils');
const { wireLog }       = require('./utils');

const wallet            = require('dcp/dcp-client/wallet');
const dcpEnv            = require('dcp/common/dcp-env');
const bearer            = require('./transports/http-bearer');
const debugging         = require('dcp/debugging').scope('dcp');

var   seqNum = 0;
var   usingDebugger = (dcpEnv.platform === 'nodejs' && requireNative('module')._cache.niim instanceof requireNative('module').Module);

/**
 * Instantiate a new Target. Targets listen for connections at the transport layer, and turns them into DCP Connections.
 * This class inherits from Reference, EventEmitter, and PseudoExpress.
 *
 * @emits bearer-listening      when we are listening on a given bearer; argument is underlying bearer Server instance
 * @emits listening             when we are listening on at least one transport
 * @emits error                 when bad things happen
 *
 * @param {string[]}    transportNames          An array of names of transports,  ["socketio", "webrtc"] to use to create
 *                                              this Target.
 * @param {object}      config                  must have .identity and .location
 * @param {function}    listeningHandler        [optional] function to invoke once each transport is
 *                                                         listening. The callback arguments are the
 *                                                         transport name and Listener instance.
 * @param {object}      options                 [optional] A DCP config options variable (like dcpConfig.dcp.connectionOptions.default)
 * @constructor
 */
exports.Target = function Target530(transportNames, config, listeningHandler, options)
{
  this.transportNames = transportNames;
  this.config = Object.assign({}, config);
  if (!this.config.location)
    this.config.location = this.config.listen;
  assert(this.config.identity, this.config.location);

  /* work out overloads */
  if (options || typeof listeningHandler === 'function')
    this.listeningHandler = listeningHandler;
  else if (!options && typeof listeningHandler === 'object')
  {
    options = listeningHandler;
    listeningHandler = undefined;
  }
  else if (listeningHandler || options)
    throw new TypeError('invalid arguments to Target constructor');

  this.debugLabel = chalk.magentaBright(`  T#${++seqNum}:`);
  this.sessionMemos = {};
  this.connectionMemos = {};
  this.url = this.config.location;
  this.isClosed = false;

  /* Create a connection config as this.options which takes into
   * account system defaults and overrides for specific urls, origins, etc.
   */
  this.options = leafMerge(
    ({
      'transports':              transportNames,
      'lingerTimeout':           3600,  /* how long a target will hope to see the initiator again */
      'noSessionCloseDelay':     1,     /* rate-limit no-session closes */
      'connectTimeout':          10,    /* max time to wait for session start */
      'connectionSweepInterval': 60,    /* often to check for connections that are eligible for closing */
    }),
    dcpConfig.dcp.connectionOptions.default,
    dcpConfig.dcp.connectionOptions[this.config.listen  .hostname],
    dcpConfig.dcp.connectionOptions[this.config.location.hostname],
    dcpConfig.dcp.connectionOptions[this.config.listen  .origin],
    dcpConfig.dcp.connectionOptions[this.config.location.origin],
    options
  );

  /* Take care of multiple inheritance details. TODO: improve this /wg dec 2023 */
  PseudoExpress.apply(this);
  const ee = new EventEmitter();
  const eep = Object.assign({}, EventEmitter.prototype, PseudoExpress.prototype);
  Object.setPrototypeOf(eep, this.constructor.prototype);
  Object.setPrototypeOf(ee, eep);
  Object.setPrototypeOf(this, ee);
  this.on('error', (error) => { debugging() && console.debug(this.debugLabel, 'event:', error)}); /* side-effect; avoid emit->throw lunacy */
  Object.assign(ee, Reference.prototype);
  Reference.call(this, debugging, () => this.debugLabel);
  this.ref();

  unlockKeystore(this.config.identity).then(ks => {
    this.config.identity = ks; /* thunk */
    this.options.identity = ks;
    this.startHttpServer();
  });

  /* If it takes more than 5s to start up, we have a very serious problem */
  this.startupWatchDogTimer = setTimeout(() => {
    if (this.isClosed)
      return;
    /** @todo remove uncatchable exception as way to terminate process here /wg Jul 2025 */
    this.emit('error', new Error('unable to start listener'));
  }, 5022 * (usingDebugger ? 10 : 1)).unref();

  /* Occasionally sweep the connection list and clean up garbage. */
  this.maintenanceIntervalHnd = setInterval(() => this.doMaintenance(), this.options.connectionSweepInterval * 1e3).unref();
  this.transportListeners = [];

  /* Add events debugging by intercepting this.emit */
  if (debugging('events'))
  {
    const inspect = (dcpEnv.platform === 'nodejs') ? requireNative('util').inspect.custom : Symbol('inspect');
    const _emit = this.emit;

    this.emit = function emitWrapper() {
      const net = requireNative('net');
      var disp;
      if (arguments[1] === undefined)
        disp = '';
      else if (arguments[1] === this)
        disp = chalk.yellow('<this>');
      else if (arguments[1][inspect])
        disp = arguments[1];
      else if (arguments[1] instanceof net.Server)
        disp = arguments[1]._connectionKey;
      else
        disp = Array.from(arguments).slice(1);

      debugging('events') && console.debug(this.debugLabel, chalk.cyan('fired event: ' + arguments[0]), disp);
      _emit.apply(this, arguments);
    };
  }
}
exports.Target.prototype = Object.create(PseudoExpress.prototype);
exports.Target.prototype.constructor = exports.Target;
Object.assign(exports.Target, PseudoExpress);

/* Add fancy console logging for Connection objects in nodejs */
if (dcpEnv.platform === 'nodejs')
{
  const inspect = requireNative('util').inspect.custom;
  exports.Target.prototype[inspect] = function Target530$$inspect() {
    return `[Object Target(${this.httpServer?.listening ? this.url : 'not listening'})]`
  };
}

/**
 * Start an HTTP server via transports/http-bearer which can be used as the underlying server for
 * multiple transports associated with this instance of Target.
 */
exports.Target.prototype.startHttpServer = function Target$$startHttpServer()
{
  assert(this.config.identity instanceof wallet.Keystore);

  if (this.isClosed)
    return;

  /* Wire up bearer - shared amongst compatible transport listeners */
  this.httpServer = bearer.httpServerFactory(this.config, (server) => {
    if (this.isClosed)
      this.httpServer.close();
    else
    {
      this.emit('bearer-listening', server);
      if (server.listening && this.url.port == 0) /* DcpURL and URL type mismatch */  // eslint-disable-line eqeqeq
        this.url.port = server.address().port;
      this.emit('listening', this);
    }
    clearTimeout(this.startupWatchDogTimer);
  }).unref();
  this.httpServer.on('error', error => this.emit('error', error));
  this.httpServer.on('dropRequest', request => console.warn('warning: dropped request', request?.method, request?.path));
  /* Wire up transport listeners. ListenerCtor dereferences this.httpServer. */
  for (let transportName of this.options.transports)
  {
    const ListenerCtor = require('./transports/' + transportName).Listener;
    const listener = new ListenerCtor(this.config, this, this.options[transportName]);
    if (this.listeningHandler)
      listener.on('listening',   (lserver) => !this.isClosed && this.listeningHandler(transportName, lserver));
    listener.on  ('listening',   (lserver) => !this.isClosed && this.transportListeners.push({ transportName, listener: lserver }));
    listener.on ('connection', (transport) => !this.isClosed && this.handleNewTransportInstance(transport));
  }

  /* Wire up /status listeners - must happen after transport listeners hook httpServer */
  const statusUrl = new DcpURL(this.config.location).resolveUrl('./status');
  this.bindServer(this.httpServer);
  this.get(statusUrl.pathname, peShowStatus);
  this.get('/status', peShowStatus); /* outer server - not usually available via proxy */
}

/**
 * API to shutdown the target and its currently-active connections.
 *
 * - Fires the end event when all connections have ended
 * - Fires the close event when all listeners have been torn down
 */
exports.Target.prototype.close = function Target$close()
{
  var ps = []; /* array of promises which always resolve and can never reject */

  if (this.isClosed)
    return;
  this.isClosed = true;

  for (let listener of this.transportListeners.map(x => x.listener))
  {
    listener.shutdown();
    listener.removeAllListeners();
  }
  clearInterval(this.maintenanceIntervalHnd);

  debugging('target') && console.debug(this.debugLabel, `closing ${this.getSessions().length} sessions`);

  for (let memo of Object.values(this.sessionMemos).concat(Object.values(this.connectionMemos)))
  {
    const conn = memo.connection;
    ps.push(new Promise((resolve, reject) => conn.on('close', resolve)));
    conn.close();
  }

  /* Prevent the server from accepting new connections and communicating with comet-style clients.
   * WebSockets still need to be shutdown explicitly by the individual transports.
   * See https://nodejs.org/api/http.html#serverclosecallback
   */
  const serverClose = {};
  serverClose.promise = new Promise(resolve => { serverClose.resolve = resolve; });
  if (this.httpServer)
    this.httpServer.close(() => serverClose.resolve());

  Promise.all(ps).then(() => {
    ps.length = 0;
    this.emit('end');

    let listenerMemo;
    while ((listenerMemo = this.transportListeners.pop()))
    {
      const listener = listenerMemo.listener;
      const p = new Promise((resolve, reject) => {
        listener.on('close', resolve);
        listener.close();
      });
      ps.push(p);
    }

    if (this.httpServer?.listening)
      ps.push(serverClose.promise);

    Promise.all(ps).then(() => {
      this.unref();
      this.emit('close');
    });
  });
}

/**
 * Internal callback which is invoked whenever a new transport instance is created
 */
exports.Target.prototype.handleNewTransportInstance = function handleNewTransportInstance(transport)
{
  const that = this;
  debugging('target') && console.debug(this.debugLabel, 'adding new transport instance');

  /* new transport instances need their messages intercepted at this layer so that
   * we can figure out if they are new DCP connections or simply reconnections.
   */
  function messageHandler(message)
  {
    if (!that.isClosed)
      that.handleFirstTransportMessage(transport, message);
    transport.off('message', messageHandler);
  }

  transport.on('message', messageHandler);
}

/**
 * Internal method that memoizes a session for later use by the reconnect code.
 */
exports.Target.prototype.memoizeSession = function memoizeSession(connection)
{
  const dcpsid = connection.dcpsid;

  assert(dcpsid);
  assert(!this.sessionMemos.hasOwnProperty(dcpsid));

  debugging('target') && console.debug(this.debugLabel, 'target registered new session', dcpsid);
  this.sessionMemos[dcpsid] = { connection, firstMessageStampMs: performance.now() };
  connection.on('end', () => { delete this.sessionMemos[dcpsid] });
  delete this.connectionMemos[connection.id];
}

/**
 * Internal method that memoizes a connection so we can expire it later if it never establishes a session.
 */
exports.Target.prototype.memoizeConnection = function memoizeConnection(connection)
{
  if (connection.dcpsid) /* Already has a session (very fast startup) - don't bother memozing */
    return;

  this.connectionMemos[connection.id] = { connection, firstMessageStampMs: performance.now() };
  debugging('target') && console.debug(this.debugLabel, 'target registered new connection', connection.id);
}

/**
 * Internal method invoked when a connection connects
 */
exports.Target.prototype.noteConnectEvent = function noteConnectEvent(connection)
{
  const sm = this.sessionMemos[connection.dcpsid];
  assert(connection.dcpsid);

  if (!sm)
  {
    debugging('target') && console.debug(this.debugLabel, `target connected after session ${connection.dcpsid} culled`);
    connection.close();
    return;
  }

  delete sm.disconnectStampMs;
}

/**
 * Internal method invoked when a connection disconnects
 */
exports.Target.prototype.noteDisconnectEvent = function noteDisconnectEvent(connection)
{
  const sm = this.sessionMemos[connection.dcpsid];

  if (!sm)
  {
    debugging('target') && console.debug(this.debugLabel, `target disconnected after session ${connection.dcpsid} culled`);
    connection.close();
    return;
  }

  sm.disconnectStampMs = performance.now();
}

/**
 * Handle the first message coming from the transport by associating it with an instance of Connection
 * and then having the Connection handle any subsequent messages.
 *
 * Once we have figured out which connection should be associated with messages from that particular
 * transport instance, we remove existing and add new event handlers to get directly without intercept.
 *
 * @param {object}    transport       a transport instance which is a child of this.transportListener
 * @param {string}    sealedMessage   Serialized Ethereum signed message - JSON-encoded version of DcpMssage or Ack
 */
exports.Target.prototype.handleFirstTransportMessage = function Target$handleFirstTransportMessage(transport, sealedMessage)
{
  var signedMessage, connection;
  debugging('wire') && wireLog(this.debugLabel, sealedMessage, 'rx');

  assert(!transport.__targetSeen);
  transport.__targetSeen = true;
  debugging('target') && console.debug(this.debugLabel, `target handling first message, ${sealedMessage.length} chars`);

  try
  {
    signedMessage = JSON.parse(sealedMessage);
    if (typeof signedMessage !== 'object' || !signedMessage.hasOwnProperty('body'))
      throw new DCPError('invalid message received from peer', 'EINVAL');

    if (!signedMessage.body.dcpsid && signedMessage.body.payload?.operation === 'connect')
      connection = this.initializeNewConnection(transport, sealedMessage, signedMessage);
    else if (signedMessage.body.dcpsid)
    {
      /* initiator thinks there exists a session - if we can find it, we'll resume it; otherwise the
       * dcpsid was for a target process which has since died -> tell initiator session is invalid
       */
      connection = this.locateExistingSession(transport, sealedMessage, signedMessage);
      if (!connection)
        this.sendNoSessionResponse(transport, sealedMessage, signedMessage);
    }
    else
    {
      console.error('received invalid "first" message from peer:', signedMessage);
    }
  }
  catch (error)
  {
    transport.close();
    this.emit('error', error);
    return;
  }

  debugging('target') && console.debug(this.debugLabel, 'associated transport instance', transport, 'with', connection);
}

/**
 * Initializes a Connection object for use by a target, based on a message from the transport layer.
 * This should be the first message from the transport connection (socket), and it must be a message
 * which contains a DCP 'connect' request.
 *
 * The message that caused this bootstrapping is turned over the the connection layer for processing.
 *
 * @param {object}   transport          the instance of Transport which originated the message
 * @param {string}   sealedMessage      [optional] the network representation of a dcp-message
 * @param {object}   signedMessage      the parsed version of sealedMessage
 * @returns {object} Connection
 */
exports.Target.prototype.initializeNewConnection = function Target$initializeConnection(transport, sealedMessage, signedMessage)
{
  assert(signedMessage.body?.type === 'request');
  const connection = new Connection(this.config, this.options);
  this.memoizeConnection(connection);
  connection.on('session',    () => { this.memoizeSession     (connection); this.emit('session', connection) });
  connection.on('connect',    () => { this.noteConnectEvent   (connection) });
  connection.on('disconnect', () => { this.noteDisconnectEvent(connection) });
  connection.accept(transport);

  this.emit('connection', connection); /* does not imply session! */
  connection.receive(sealedMessage, signedMessage);

  return connection;
}

/**
 * @param {object}    transport       a transport instance which is a child of this.transportListener
 * @param {string}    message         DCP session id sent in the first message from the peer
 *
 * @returns instance of Connection or false
 */
exports.Target.prototype.locateExistingSession = function Target$locateExistingSession(transport, sealedMessage, signedMessage)
{
  const dcpsid = signedMessage.body.dcpsid;
  var sm;

  assert(transport, dcpsid);
  this.doMaintenance();
  sm = this.sessionMemos[dcpsid];

  if (!sm)
  {
    debugging('target') && console.debug(this.debugLabel, `target received message after session ${dcpsid} culled or target restarted`);
    return;
  }

  debugging('target') && console.debug(this.debugLabel, 'message is for session', dcpsid);
  if (sm.connection.transport)
  {
    sm.connection.transport.close();
    sm.connection.transport = false;
  }
  sm.connection.attach(transport);
  sm.connection.resumeSession(sealedMessage, signedMessage);

  return sm.connection;
}

/**
 * This function runs when a peer's transport connects to us, but we don't have a corresponding session
 * in memory.  This means that any application state that the peer was counting on does not exist in this
 * target, and the usual (but not only) way this happens is when the target daemon either crashes or is
 * recycled for maintenance.
 *
 * We bootstrap enough of a session to emit a close Request, using information from the peer's message.
 * No special OOB messaging, magic message types, or security-bypass schemes are necessary for this.
 * That said, we also don't bother authenticating the crypto on the message, etc, because we don't care:
 * it is never associated with anything outside of this function, and the only thing we do with the
 * newly-created session is close it.
 *
 * @param {object} transport       instance of an underlying transport
 * @param {object} signedMessage   raw message sent over the transport, after parsing from JSON
 */
exports.Target.prototype.sendNoSessionResponse = async function Target530$$sendNoSessionResponse(transport, sealedMessage, signedMessage)
{
  const connection = new Connection(this.config, this.options).unref();
  const dcpsid = signedMessage.body.dcpsid;

  (debugging('target') || debugging('connection')) && console.debug(this.debugLabel, `no such session, telling peer to close ${dcpsid}`);

  try
  {
    /* Peers should send us the last Ack they sent, and then the last message transmitted, if it was
     * not unack'd. Peers with version < 5.3 might send us the last Message even if it was Ack'd. This
     * is a bug and attempt to work around problems described in DCP-3957.
     *
     * It's possible that transport-level buffers will carry other messages as well, so we can't be
     * certain this signedMessage is an ack - so wait a short time for an ack before simply banging
     * the connection shut. The Ack message in this context is needed to get a nonce we can use to tell
     * the remote to close.
     *
     * In the case where the transport buffer spews so much stuff that the timer expires,
     * the peer will wind up yoyoing until the transport buffer is empty and it eventually
     * sends an ack, then finally gets the close request which will cause it to stop
     * reconnecting.
     *
     * We never emit the Connection<session> event for this fake session, so we should never have the
     * application layer adding Connection<request> event emitters that could catch requests on this
     * fake session.
     *
     * Close message synchronization isn't really important, except that it tells the peer
     * end that we closed the transport-level connection on purpose and that trying to
     * reconnect is futile.
     */
    connection.peerAddress = new wallet.Address(signedMessage.owner);
    connection.dcpsid = dcpsid;
    connection.hasNtp = true;
    connection.role.set('unspecified', 'target');

    connection.state.set('initial', 'established');
    connection.transport = transport;

    switch(signedMessage.body.type)
    {
      default:
        (debugging('target') || debugging('connection')) && console.debug(this.debugLabel, `ignoring incoming ${signedMessage.body.type} during no-session reconnect`);
        break;
      case 'request':
      {
        const operation = signedMessage.body.payload?.operation;
        if (operation !== 'connect' && operation !== 'close' && operation !== 'keepalive')
        {
          (debugging('target') || debugging('connection')) && console.debug(this.debugLabel, `ignoring incoming ${operation} request during no-session reconnect`);
          break;
        }
        /* fallthrough */
      }
      case 'notification':
        if (signedMessage.body.type === 'notification')
          (debugging('target') || debugging('connection')) && console.debug(this.debugLabel, `ignoring incoming notification during no-session reconnect`);
        /* fallthrough */
      case 'ack':
        connection.lastRxNonce = signedMessage.body.nonce;
        connection.lastRxAckToken = signedMessage.body.token;
        break;
    }
  }
  catch(error)
  {
    console.error('Error during sendNoSessionResponse:', error);
  }
  finally
  {
    /* assert ensures we never accidentally leak this session outward in future code */
    assert(connection.listeners('request').length === 0);
    assert(connection.listeners('notification').length === 0);

    /* Delay close operation to rate-limit endless loops caused by broken initiators, but try to not
     * sleep if the process is exiting (implied by the fact that the Target instance has been asked to
     * close).
     */
    const sleepHnd = a$sleep( ((0.50 * this.options.noSessionCloseDelay) * Math.random())
                             + (0.75 * this.options.noSessionCloseDelay));

    connection.on('end',   () => sleepHnd.intr());
    connection.on('close', () => sleepHnd.intr());

    if (!this.isClosed)
      await sleepHnd;
    connection.close();
  }
}

/**
 * Retrieve a list of connections' dcpsids and their statuses.
 * @returns {Array} list of { dcpsid, status }
 */
exports.Target.prototype.getSessions = function Target530$$getSessions()
{
  return Object.values(this.sessionMemos).map(memo => ({ dcpsid: memo.dcpsid }));
}

/**
 * Remove all connections from the target's memo list that have not have been
 * disconnected for more than lingerTimeout seconds and close connections which
 * have not established a session without connectTimeout seconds.
 */
exports.Target.prototype.doMaintenance = function Target$doMaintenance()
{
  const now = performance.now();
  debugging('target') && debugging('verbose') && console.debug(this.debugLabel, 'performing connection maintenance');

  for (let id in this.connectionMemos)
  {
    const cm = this.connectionMemos[id];

    if (cm.hasOwnProperty('firstMessageStampMs') && (now - cm.firstMessageStampMs) > cm.connection.options.connectTimeout * 1E3)
    {
      debugging('target') && console.debug(this.debugLabel, 'dropping connection which never established session');
      cm.connection.close(true);
      delete this.connectionMemos[id];
    }
  }

  for (let dcpsid in this.sessionMemos)
  {
    const sm = this.sessionMemos[dcpsid];
    if (!sm.hasOwnProperty('disconnectStampMs')) /* session is not disconnected */
      continue;
    const timeoutMs = sm.connection.options.lingerTimeout * 1000;

    if (now - sm.disconnectStampMs > timeoutMs)
    {
      debugging('target') && console.debug(`${this.debugLabel} culling session ${dcpsid}`);
      sm.connection.shutdown(new DCPError('session exceeded linger timeout', 'ETIMEDOUT'));
      delete this.sessionMemos[dcpsid];
    }
  }
}

/** Used for tests, not API */
exports.Target.transportRequire = mid => require(require('path').join(module.path, 'transports', mid));

async function unlockKeystore(idKs)
{
  if (idKs instanceof Promise)
    idKs = await idKs;
  debugging('target') && console.debug('unlocking keystore', idKs.label || idKs.address);
  assert(idKs instanceof wallet.Keystore);
  const pk = await idKs.getPrivateKey(); /* trigger password prompt */
  return new wallet.Keystore(pk, '');
}

/**
 * A handler for PseudoExpress which displays the status of the Target's process
 * @param {Object} request      the http request that was sent to the PseudoExpress server
 * @param {Object} response     the http response that will be sent back to the client
 * @returns false
 */
function peShowStatus(request, response)
{
  const process = requireNative('process');
  const os      = requireNative('os');

  function fancy(object)
  {
    const padLen  = 4 + Object.keys(object).reduce((curMax, key) => key.length > curMax ? key.length : curMax,  0);
    return '\n' + Object.entries(object).map(([key, value]) => ('  ' + key + ': ').padEnd(padLen) + value).join('\n');
  }

  response.status = 200;
  response.set('content-type', 'text/plain; charset=utf8');
  response.set('cache-control', 'no-cache');

  const output = `${new Date()}\n
request:        ${request.method} ${request.url}
program:        ${process.argv[1]}
pid:            ${process.pid}
debugPort:      ${process.debugPort}
hostname:       ${request.hostname} aka ${os.hostname()}
uptime:         ${process.uptime()}
loadavg:        ${os.loadavg()}
resources:      ${fancy(process.resourceUsage())}
memory:         ${fancy(process.memoryUsage())}
totalmem:       ${os.totalmem()}
headers:        ${fancy(request.headers)}

body:         \n${request.body || ''}
`;

  response.send(output);
}
